import { Barcode, ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import type * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { fmtDate, int0, plural } from "../format";
import { canPickItem } from "./changeModel";
import type { PlaceEditActions } from "./useOzonDocActions";

/** Таблица мест, панель выбранного и перенос части количества.
 *
 *  Правка — это ВЫБОР, и форма выбора ОДНА: отмечается место целиком или один артикул внутри
 *  раскрытой паллеты. На этом выборе три действия — «Собрать» (в новую паллету или в
 *  существующую), «Разобрать на короба» и «Перенести часть…». Последнее включается только когда
 *  выбрана ровно одна строка: доля количества — единственное, что выбором не выражается.
 */

/** Выбор и то, что от него зависит. Живёт в редакторе, а не тут: его же сбрасывает любая правка
 *  раскладки (после неё номера мест другие, и старый выбор означал бы не те места). */
export interface Selection {
  picked: ozon.OzonPick[];
  setPicked: (v: ozon.OzonPick[]) => void;
  /** назначение сборки; null — новая паллета */
  dest: number | null;
  setDest: (v: number | null) => void;
  /** открыт перенос части количества */
  parting: boolean;
  setParting: (v: boolean) => void;
}

/** Панель выбранного: что выбрано и что с этим можно сделать. Пока не выбрано ничего — её нет. */
export function PickPanel({
  places,
  sel,
  busy,
  edits,
  changeMode = false,
}: {
  places: ozon.SavedPlace[];
  sel: Selection;
  busy: boolean;
  edits: PlaceEditActions;
  /** идёт правка состава: из места с кодом можно ВЫНЕСТИ товар (целиком), а коды снимает каскад */
  changeMode?: boolean;
}): React.ReactElement | null {
  const { picked, setPicked, dest, setDest, parting, setParting } = sel;
  // куда можно собрать: паллеты, кроме тех, что выбраны целиком (собирать место в себя же
  // нечего, и бэкенд на это отвечает отказом). ⚠️ В режиме изменений — и паллеты С КОДОМ: класть
  // в отправленную паллету с коробами бэкенд разрешает (`ozon_places._as_target`: новые строки
  // уедут своими коробами на ту же транспортную паллету). Пока их тут отсекали, недогруженную
  // вторую паллету нельзя было слить в первую, уже отправленную (#339, 27.10.2026). Место старого
  // образца (код на самом месте) бэкенд отвергнет словами — своей проверки тут не заводим
  const livePallets = places.filter(
    (t) => t.kind === "PALLET" && (changeMode || !t.cargo_id),
  );
  // собирать в место, выбранное целиком, нечего: оно и есть то, что собирают
  const targets = livePallets.filter(
    (t) =>
      !picked.some((x) => x.position === t.position && x.sku === undefined),
  );
  // перенос ЧАСТИ количества — единственное, что выбором не выражается, поэтому он включается
  // только когда выбрана ровно одна позиция и понятно, что именно двигаем
  const part = onlyItem(places, picked);
  // ⚠️ Выбранное назначение могло исчезнуть из списка (его отметили целиком, или оно уехало на
  // площадку фоновым обновлением). Браузер тогда показывает «на новую паллету», а `dest` продолжал
  // держать старый номер — экран говорил одно, а запрос уходил с другим
  const toPallet = targets.some((t) => t.position === dest) ? dest : null;
  // ⚠️ Заморожено МЕСТО, а не поставка: площадка не даёт удалить все места разом, значит хотя бы
  // один код есть всегда — и запрет «в поставке есть отправленное» запирал бы раскладку навсегда,
  // вместе с кнопкой «Снять коды». Досылка «собрали часть, отправили, добавили ещё» ради этого и
  // есть. Собирать и разбирать можно только неотправленное; снимать коды — наоборот, только его.
  const codedPicked = picked.some(
    (x) => places.find((t) => t.position === x.position)?.cargo_id != null,
  );
  // ⚠️ В режиме изменений из места с кодом можно ВЫНЕСТИ товар — целиком, — а в паллету с кодом
  // и коробами можно класть. Остальное (менять тип, переподбирать партии, класть в короб с кодом)
  // по-прежнему откажет бэкенд, и отказ он называет словами — своих проверок тут не заводим
  const editable = !busy && (changeMode || !codedPicked);
  if (picked.length === 0) return null;
  return (
    <div className="mb-1.5 space-y-1 rounded bg-muted/60 px-2 py-1">
      <div className="flex flex-wrap items-center gap-2">
        <b>Выбрано: {plural(picked.length, "позиция", "позиции", "позиций")}</b>
        {codedPicked && (
          <span className="text-amber-600 dark:text-amber-400">
            {changeMode
              ? "среди выбранного есть отправленные места — из них товар только ВЫНОСИТСЯ, целиком"
              : "среди выбранного есть отправленные места — их правят только снятием кодов"}
          </span>
        )}
        {/* ⚠️ Глагол ПЕРЕД назначением, и назначение вплотную к своей кнопке: пока селект
                стоял первым, он выглядел общим для всех трёх действий, хотя относится только к
                сборке. Читается одной фразой: «Собрать · в новую паллету» */}
        <Button
          size="sm"
          variant="outline"
          disabled={!editable}
          onClick={() =>
            edits.gather.mutate({ picks: picked, to_position: toPallet })
          }
          title="Сложить всё выбранное в ОДНО место — в новую паллету или в ту, что выбрана справа (наш предел — 48 коробов на паллету)"
        >
          Собрать
        </Button>
        <select
          value={toPallet ?? ""}
          disabled={!editable}
          onChange={(e) =>
            setDest(e.target.value === "" ? null : Number(e.target.value))
          }
          className="-ml-1 h-7 rounded border border-input bg-background px-1"
          title="Куда собрать выбранное. Новая паллета создаётся сразу с товаром — пустых мест в раскладке не бывает."
        >
          <option value="">в новую паллету</option>
          {targets.map((t) => (
            <option key={t.position} value={t.position}>
              в паллету №{t.position}
            </option>
          ))}
        </select>
        <span className="text-muted-foreground/50">|</span>
        <Button
          size="sm"
          variant="outline"
          disabled={!editable}
          onClick={() => edits.boxes.mutate(picked)}
          title="Обратное сборке: рассыпать выбранное на заводские короба — по коробу на место, у каждого свой код площадки"
        >
          Разобрать на короба
        </Button>
        {part !== null && part.item.qty > 1 && (
          <Button
            size="sm"
            variant="outline"
            disabled={!editable}
            className={cn(parting && "bg-accent")}
            onClick={() => setParting(!parting)}
            title="Отделить ЧАСТЬ количества (например 50 из 90 шт) — единственное, что не выражается выбором"
          >
            Перенести часть…
          </Button>
        )}
        {/* ⚠️ Только когда в выборе ЕСТЬ места с кодом: снимать нечего — кнопка обещала бы
            действие, на которое площадка ответит отказом, и стояла она рядом с «Собрать»,
            которое как раз по местам с кодом не работает */}
        {codedPicked && (
          <Button
            size="sm"
            variant="outline"
            // ⚠️ В режиме изменений коды снимает КАСКАД, и снять их руками — значит удалить
            // место у площадки мимо плана: `cargoes.drop` при активном заходе и откажет.
            // Кнопка не исчезает, а гаснет с причиной — иначе её ищут глазами
            disabled={busy || changeMode}
            title={
              changeMode
                ? "Идёт изменение состава: коды опустевших мест снимет применение правки"
                : "Удалить грузоместа на площадке и вернуть их в правку"
            }
            onClick={() => {
              if (
                window.confirm(
                  "Снять коды у выбранных мест? Они будут удалены на площадке, а напечатанные " +
                    "этикетки станут недействительны.",
                )
              )
                edits.drop.mutate([...new Set(picked.map((x) => x.position))]);
            }}
          >
            Снять коды
          </Button>
        )}
        <button
          type="button"
          className="text-muted-foreground hover:underline"
          onClick={() => setPicked([])}
        >
          снять выделение
        </button>
      </div>
      {parting && part !== null && (
        <PartialMove
          from={part.place}
          item={part.item}
          // ⚠️ свой список: собирать нельзя в выбранные места, а переносить — в исходное.
          // `targets` предлагал паллету, из которой товар и берут, и площадка отвечала отказом
          pallets={livePallets.filter(
            (t) => t.position !== part.place.position,
          )}
          busy={busy}
          onMove={(v) => edits.move.mutate(v)}
        />
      )}
    </div>
  );
}

/** Места списком: место = СТРОКА, паллета раскрывается по стрелке.
 *
 *  Единица работы склада — заводской короб, и мест этих десятки. Пока каждое место рисовалось
 *  карточкой с таблицей внутри, раскладка на тринадцать коробов занимала два экрана и повторяла
 *  шапку «Артикул / Штук / Коробов» тринадцать раз — при том что в коробе по определению один
 *  артикул и таблице там нечего показывать.
 */
export function PlacesTable({
  places,
  transport,
  fetching,
  picked,
  open,
  togglePick,
  toggleOpen,
  renderItem,
  changeMode = false,
}: {
  places: ozon.SavedPlace[];
  /** Правки ОДНОЙ строки товара — своей колонкой справа; `undefined` — колонки нет вовсе.
   *
   *  ⚠️ Таблица их не знает и знать не должна: «убрать» и «заменить партию» существуют только в
   *  режиме изменений, и решает это страница. Колонка появляется вместе с ними — пустая, она
   *  обещала бы действие, которого сейчас нет. */
  renderItem?: (
    place: ozon.SavedPlace,
    item: ozon.SavedItem,
  ) => React.ReactNode;
  /** поставка с грузоместами: площадка требует ставить на паллеты ВСЕ коробки */
  transport: boolean;
  fetching: boolean;
  picked: ozon.OzonPick[];
  /** номера раскрытых паллет */
  open: number[];
  togglePick: (p: ozon.OzonPick) => void;
  toggleOpen: (pos: number) => void;
  /** идёт правка состава: из места с кодом строку можно ВЫНЕСТИ — значит и выбрать (`canPickItem`) */
  changeMode?: boolean;
}): React.ReactElement {
  return (
    <>
      {places.length === 0 && !fetching && (
        <div className="text-muted-foreground">
          Мест ещё нет — нажмите «Разложить».
        </div>
      )}

      {places.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse">
            <colgroup>
              <col className="w-7" />
              <col className="w-8" />
              <col className="w-24" />
              <col className="w-24" />
              <col className="w-16" />
              {/* колонка держит разбор неполного короба целиком — «40 + 1 (13/15)» */}
              <col className="w-32" />
              <col className="w-20" />
              <col className="w-24" />
              <col className="w-24" />
              <col />
              {/* коды коробок — иконка со счётчиком, места надо немного */}
              <col className="w-16" />
              {/* код площадки 19-значный: в узкой колонке он переносился и обрезался */}
              <col className="w-40" />
              {renderItem && <col className="w-8" />}
            </colgroup>
            <thead className="text-muted-foreground">
              <tr className="border-b text-left">
                <th />
                <th className="py-1 font-normal">№</th>
                <th className="py-1 font-normal">Тип</th>
                <th className="py-1 font-normal">Артикул</th>
                <th className="py-1 text-right font-normal">Штук</th>
                <th className="py-1 text-right font-normal">Коробов</th>
                <th className="py-1 pl-3 font-normal">Партия №</th>
                <th className="py-1 font-normal">Годен до</th>
                <th className="py-1 font-normal" title="Характеристика (упаковка/канал) пула, из которого подобрана партия">Хар.</th>
                <th className="py-1 font-normal">Наименование</th>
                <th className="py-1 text-right font-normal">Коды</th>
                <th />
                {renderItem && <th />}
              </tr>
            </thead>
            <tbody>
              {places.map((p) => {
                const single = p.items.length === 1 ? p.items[0]! : null;
                const empty = p.items.length === 0;
                const expanded = open.includes(p.position);
                return (
                  <React.Fragment key={p.position}>
                    <tr className="border-b last:border-0 hover:bg-accent/40">
                      <PickCell
                        pick={{ position: p.position }}
                        picked={picked}
                        on={togglePick}
                      />
                      <td className="py-1 tabular-nums">{p.position}</td>
                      <td className="py-1">
                        {single === null && p.items.length > 0 ? (
                          <button
                            type="button"
                            className="flex items-center gap-0.5 hover:underline"
                            onClick={() => toggleOpen(p.position)}
                          >
                            {expanded ? (
                              <ChevronDown className="size-3.5" />
                            ) : (
                              <ChevronRight className="size-3.5" />
                            )}
                            {kindLabel(p.kind)}
                          </button>
                        ) : (
                          <span className="pl-4">{kindLabel(p.kind)}</span>
                        )}
                        {/* ⚠️ В поставке с грузоместами площадка требует ставить на паллеты ВСЕ
                            коробки. Место вне паллеты уедет непривязанным, и подготовка у неё
                            останется незаконченной — молча. Видно должно быть сразу, а не
                            отказом при отправке, где коды уже выданы */}
                        {transport && p.kind !== "PALLET" && (
                          <span
                            className="ml-1 rounded bg-red-100 px-1 text-red-700 dark:bg-red-950 dark:text-red-300"
                            title="Поставка с грузоместами: все коробки должны стоять на паллетах. Соберите это место на паллету."
                          >
                            не на паллете
                          </span>
                        )}
                      </td>
                      {single ? (
                        <ItemCells item={single} />
                      ) : (
                        <>
                          <td className="py-1 text-muted-foreground">
                            {empty ? "пусто" : `${p.items.length} артикула`}
                          </td>
                          <td className="py-1 text-right tabular-nums">
                            {int0(p.qty)}
                          </td>
                          <td className="py-1 text-right tabular-nums">
                            {p.boxes === null ? "—" : int0(p.boxes)}
                          </td>
                          {/* ⚠️ Раньше здесь висело «микс — приёмка платная». Платной была
                              палета-МИКС, то есть палета, отправленная ГРУЗОМЕСТОМ. Теперь
                              грузоместом едет коробка (моно), а палета — транспортное место, и
                              несколько артикулов на ней площадке безразличны. Предупреждение
                              гнало дробить раскладку без причины. */}
                          <td
                            className="py-1 pl-3 text-muted-foreground"
                            colSpan={5}
                          />
                        </>
                      )}
                      <td className="py-1 text-right">
                        {p.cargo_id && (
                          <span
                            className="whitespace-nowrap tabular-nums text-muted-foreground"
                            // грузоместо у площадки — это КОРОБКА, у каждой свой код и своя
                            // этикетка. У палеты код транспортного места, и наклейка на неё
                            // печатается своей кнопкой — «Наклейки палет»
                            title={
                              p.transport_cargo_id
                                ? "транспортное место (палета): коробки на ней с отдельными кодами, наклейка — кнопкой «Наклейки палет»"
                                : "код грузоместа площадки"
                            }
                          >
                            {/* хвост кода, как выделяет его сама площадка на этикетке: полные
                                19 знаков в колонку не влезают и обрезаются многоточием, а
                                сверяют люди именно последние цифры */}
                            {p.transport_cargo_id ? "палета" : "код"} …
                            {p.cargo_id.slice(-6)}
                          </span>
                        )}
                      </td>
                      {/* ⚠️ Правки строки — только там, где строка ОДНА: у места с несколькими
                          артикулами «убрать» без указания, что именно, означало бы не то место.
                          Такие места раскрываются, и правки стоят на их строках */}
                      {renderItem && (
                        <td className="py-1 text-right">
                          {single && renderItem(p, single)}
                        </td>
                      )}
                    </tr>

                    {single === null &&
                      expanded &&
                      p.items.map((i) => (
                        // ⚠️ ключ по товару И ПАРТИИ: строк у товара столько, сколько партий его
                        // закрывают, и один ключ на две строки заставляет React путать их между
                        // собой при перерисовке
                        <tr
                          key={`${i.mp_sku}-${i.batch_no ?? ""}`}
                          className="border-b bg-muted/30 last:border-0"
                        >
                          {/* товар внутри паллеты выбирается так же, как место: одна форма выбора
                              на всё, иначе снять артикул с микс-паллеты можно только переносом */}
                          <PickCell
                            pick={{ position: p.position, sku: i.mp_sku }}
                            picked={picked}
                            on={togglePick}
                            // ⚠️ Вне режима: строку внутри уехавшего места не переложить, а само
                            // место выбрать надо — иначе с него не снять коды. В режиме запрет
                            // снят — вынести строку ЦЕЛИКОМ из места с кодом разрешено, и без
                            // этого выбора снять с места один артикул из нескольких нечем
                            disabled={!canPickItem(p.cargo_id, changeMode)}
                            indent
                          />
                          <td colSpan={2} />
                          <ItemCells item={i} />
                          <td />
                          {renderItem && (
                            <td className="py-1 text-right">
                              {renderItem(p, i)}
                            </td>
                          )}
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
const samePick = (a: ozon.OzonPick, b: ozon.OzonPick): boolean =>
  a.position === b.position && (a.sku || "") === (b.sku || "");

const isPicked = (list: ozon.OzonPick[], pick: ozon.OzonPick): boolean =>
  list.some((x) => samePick(x, pick));

/** Добавить выбор, не оставляя противоречий: место и его же строка — это один и тот же товар,
 *  выбранный дважды, и отправить такое на сервер значит попросить перенести его два раза. */
const add = (list: ozon.OzonPick[], pick: ozon.OzonPick): ozon.OzonPick[] =>
  pick.sku
    ? [...list.filter((x) => !(x.position === pick.position && !x.sku)), pick]
    : [...list.filter((x) => x.position !== pick.position), pick];

/** Одна выбранная СТРОКА — или ничего. По ней включается перенос части количества: двигать часть
 *  можно только у чего-то одного, иначе непонятно, часть чего. */
function onlyItem(
  places: ozon.SavedPlace[],
  picked: ozon.OzonPick[],
): { place: ozon.SavedPlace; item: ozon.SavedItem } | null {
  if (picked.length !== 1) return null;
  const pick = picked[0]!;
  const place = places.find((p) => p.position === pick.position);
  if (!place || place.cargo_id) return null;
  const item = pick.sku
    ? place.items.find((i) => i.mp_sku === pick.sku)
    : place.items.length === 1
      ? place.items[0]
      : undefined;
  return item ? { place, item } : null;
}

/** Ячейка выбора. Одна на оба вида строк: место и артикул внутри раскрытой паллеты выбираются
 *  одинаково — форма выбора в разделе ровно одна. */
function PickCell({
  pick,
  picked,
  on,
  disabled = false,
  indent = false,
}: {
  pick: ozon.OzonPick;
  picked: ozon.OzonPick[];
  on: (p: ozon.OzonPick) => void;
  disabled?: boolean;
  indent?: boolean;
}): React.ReactElement {
  return (
    <td className={cn("py-1", indent && "pl-3")}>
      <input
        type="checkbox"
        disabled={disabled}
        checked={isPicked(picked, pick)}
        onChange={() => on(pick)}
      />
    </td>
  );
}

/** Ячейки одной строки состава — одинаковые у места-короба и у товара внутри паллеты. */
function ItemCells({ item }: { item: ozon.SavedItem }): React.ReactElement {
  return (
    <>
      <td className="truncate py-1" title={item.article}>
        {item.article}
      </td>
      <td className="py-1 text-right tabular-nums">{int0(item.qty)}</td>
      {/* ⚠️ Неполный короб называем вслух: площадка примет место на любое количество, но на складе
          такой короб вскрывают и пересчитывают — кладовщик должен знать это ДО сборки, а не по
          факту. Подрезать состав до целых коробов можно было раньше, на «Составе» до заявки. */}
      <td className="whitespace-nowrap py-1 text-right tabular-nums">
        {item.boxes === null ? (
          "—"
        ) : (
          <>
            {/* ⚠️ Пишем словами арифметики: «7 + 1 (9/15)» — семь полных коробов и неполный, в
                нём 9 штук из 15. Сокращения не прижились: «(посл. 9/15)» не влезало и наезжало
                на «Партию», «1·9» читалось диапазоном «от 1 до 9», а голая звёздочка требовала
                наводить мышь на каждую строку. Неполных коробов стало заметно больше: подбор
                режет строку по партиям, и у каждой партии свой хвостовой короб. */}
            {item.in_box > 0 && item.qty % item.in_box > 0 ? (
              <>
                {Math.floor(item.qty / item.in_box) > 0 && (
                  <>{int0(Math.floor(item.qty / item.in_box))} + </>
                )}
                1{" "}
                <span
                  className="text-amber-600 dark:text-amber-400"
                  title={`Неполный короб: ${item.qty % item.in_box} шт вместо ${item.in_box}`}
                >
                  ({item.qty % item.in_box}/{item.in_box})
                </span>
              </>
            ) : (
              int0(item.boxes)
            )}
          </>
        )}
      </td>
      {/* партия — не справка: она уезжает внутри коробки и печатается на её этикетке. Строк у
          товара столько, сколько партий его закрывает */}
      <td className="py-1 pl-3 tabular-nums">
        {item.batch_no ?? (
          <span className="text-amber-600 dark:text-amber-400">нет</span>
        )}
      </td>
      <td className="whitespace-nowrap py-1 tabular-nums text-muted-foreground">
        {item.batch_exp ? fmtDate(item.batch_exp) : "—"}
      </td>
      {/* канал пула партии: null — партия не подобрана (или до учёта характеристики), "" — пустая
          характеристика («все МП»), иначе — название канала (OZON/WB/…) */}
      <td className="whitespace-nowrap py-1 text-muted-foreground">
        {item.channel === null ? (
          "—"
        ) : item.channel === "" ? (
          <span className="text-muted-foreground/60">пустая</span>
        ) : (
          item.channel
        )}
      </td>
      <td
        className="truncate py-1 pr-2 text-muted-foreground"
        title={item.name}
      >
        {item.name}
      </td>
      <td className="py-1 pr-2 text-right">
        <BoxCodes codes={item.cargo_ids} />
      </td>
    </>
  );
}

/** Коды коробок строки — по одному на физический короб, за иконкой.
 *
 *  ⚠️ Показывать их суммой нельзя: код у КАЖДОЙ коробки свой, по нему её ищут на складе и по нему
 *  печатается этикетка. Но и разворачивать их в таблицу незачем — в боевой поставке коробок
 *  бывает 300+, и список превратил бы экран в простыню. Поэтому иконка с числом, а сами коды —
 *  в раскрывающемся списке.
 */
function BoxCodes({ codes }: { codes: string[] }): React.ReactElement | null {
  if (codes.length === 0) return null;
  return (
    // ⚠️ Поповер из `ui/popover` (radix), а не свой `absolute`-блок: таблица лежит внутри
    // `overflow-x-auto`, и собственный блок ею же обрезался бы по краю, да ещё не закрывался ни
    // по Esc, ни по клику мимо. Портал radix решает ровно это.
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded px-1 tabular-nums text-muted-foreground hover:bg-accent"
          title={`Коды коробок: ${codes.length}. Нажмите, чтобы увидеть список`}
        >
          <Barcode className="size-3.5" />
          {codes.length}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-64 w-64 overflow-auto p-2 text-xs"
      >
        <div className="mb-1 text-muted-foreground">
          коробок {codes.length}, коды:
        </div>
        {codes.map((code, n) => (
          <div key={code} className="flex justify-between gap-2 tabular-nums">
            <span className="text-muted-foreground">{n + 1}</span>
            <span>{code}</span>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** Перенос ЧАСТИ количества — единственное действие, которое выбором не выражается.
 *
 *  Назначение нарочно короткое: новая паллета, новый короб или существующая паллета. «В короб
 *  №N» отсюда убрано — короб держит один артикул, и перенос чужого товара в него всё равно тут же
 *  выносился в отдельный короб, то есть делал НЕ то, что написано на кнопке.
 */
function PartialMove({
  from,
  item,
  pallets,
  busy,
  onMove,
}: {
  from: ozon.SavedPlace;
  item: ozon.SavedItem;
  pallets: ozon.SavedPlace[];
  busy: boolean;
  onMove: (v: {
    sku: string;
    qty: number;
    from_position: number;
    to_position: number | null;
    to_kind?: string;
  }) => void;
}): React.ReactElement {
  const [qty, setQty] = useState(Math.max(1, item.qty - 1));
  const [to, setTo] = useState("new-PALLET");
  return (
    <div className="flex flex-wrap items-center gap-2 border-t pt-1">
      <span>
        Из места {from.position}: <b>{item.article}</b>, всего {int0(item.qty)}{" "}
        шт — перенести
      </span>
      <input
        type="number"
        min={1}
        max={item.qty - 1}
        value={qty}
        onChange={(e) => setQty(Number(e.target.value))}
        className="h-6 w-20 rounded border bg-background px-1 text-right tabular-nums"
      />
      <span>шт →</span>
      <select
        value={to}
        onChange={(e) => setTo(e.target.value)}
        className="h-6 rounded border bg-background px-1"
      >
        <option value="new-PALLET">в новую паллету</option>
        <option value="new-BOX">в новые короба</option>
        {pallets.map((t) => (
          <option key={t.position} value={String(t.position)}>
            в паллету №{t.position}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        disabled={busy || qty <= 0 || qty >= item.qty}
        onClick={() => {
          const isNew = to.startsWith("new-");
          onMove({
            sku: item.mp_sku,
            qty,
            from_position: from.position,
            to_position: isNew ? null : Number(to),
            to_kind: isNew ? to.slice(4) : undefined,
          });
        }}
      >
        Перенести
      </Button>
    </div>
  );
}

function kindLabel(kind: string): string {
  return kind === "PALLET" ? "Паллета" : "Короб";
}

/** Переключить выбор, не оставляя противоречий. */
export const withPick = (
  list: ozon.OzonPick[],
  pick: ozon.OzonPick,
): ozon.OzonPick[] =>
  isPicked(list, pick)
    ? list.filter((x) => !samePick(x, pick))
    : add(list, pick);
